/**
 * 从 msOrg 获取当前登录主体在 staff_stores 中可访问的门店 ID（与中台 pms_hotel_cache.id 对齐）。
 * 依赖与企微换票一致的 Bearer（localStorage/sessionStorage `token`）。
 */
import axios from "axios";
import { recordApiErrorFromAxios } from "../utils/apiLastError";
import { getMsOrgApiBaseUrl } from "./wecomAuthService";

function readBearerToken(): string | null {
  return (
    localStorage.getItem("token")?.trim() ||
    sessionStorage.getItem("token")?.trim() ||
    null
  );
}

/** GET /auth/me 与 GET /staff/:id/stores 的成功体（msOrg successResponse） */
interface MsOrgMeResponse {
  success?: boolean;
  user?: { id?: string };
}

interface MsOrgStaffStoresResponse {
  success?: boolean;
  data?: { storeIds?: unknown };
}

/**
 * 拉取当前用户可访问的门店 ID 列表（数字，与中台 hotel id 一致）。
 * 无 token、非 staff、或接口失败时返回 []。
 */
export async function fetchAccessibleStoreIdsFromMsOrg(
  tokenOverride?: string | null
): Promise<number[]> {
  const token = (tokenOverride ?? readBearerToken())?.trim();
  if (!token) return [];

  const base = getMsOrgApiBaseUrl();
  /** 禁止浏览器/中间层缓存「当前用户」与门店授权（GET 易被误缓存） */
  const noStoreHeaders = {
    Authorization: `Bearer ${token}`,
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
  } as const;
  const cacheBust = { _: String(Date.now()) };

  try {
    const meRes = await axios.get<MsOrgMeResponse>(`${base}/auth/me`, {
      headers: noStoreHeaders,
      params: cacheBust,
      timeout: 15000,
    });
    if (!meRes.data?.success || !meRes.data?.user?.id) {
      return [];
    }
    const principalId = String(meRes.data.user.id).trim();
    if (!principalId) return [];

    const storesRes = await axios.get<MsOrgStaffStoresResponse>(
      `${base}/staff/${encodeURIComponent(principalId)}/stores`,
      {
        headers: noStoreHeaders,
        params: cacheBust,
        timeout: 15000,
      }
    );
    if (!storesRes.data?.success || storesRes.data.data == null) {
      return [];
    }
    const raw = storesRes.data.data.storeIds;
    if (!Array.isArray(raw)) return [];
    const ids: number[] = [];
    for (const s of raw) {
      const n = Number.parseInt(String(s).trim(), 10);
      if (Number.isFinite(n)) ids.push(n);
    }
    return [...new Set(ids)];
  } catch (e) {
    recordApiErrorFromAxios(e);
    return [];
  }
}
